import { Injectable } from '@angular/core';
import { driver, DriveStep, Driver } from 'driver.js';
import { BehaviorSubject } from 'rxjs';

const TOUR_KEY_PREFIX = 'h2s_tour_seen_';

@Injectable({ providedIn: 'root' })
export class TourService {
  private driverInstance: Driver | null = null;

  /** True while any tour/hint overlay is showing. The floating guide
   *  widgets watch this to hide themselves — otherwise they can sit on
   *  top of (and hide) the very element a hint is pointing at. */
  readonly active$ = new BehaviorSubject<boolean>(false);

  startTour(tourId: string, steps: DriveStep[]): void {
    if (this.hasSeenTour(tourId) || !steps.length) return;
    this.launchTour(tourId, steps);
  }

  replayTour(tourId: string, steps: DriveStep[]): void {
    this.resetTour(tourId);
    this.launchTour(tourId, steps);
  }

  hasSeenTour(tourId: string): boolean {
    return localStorage.getItem(TOUR_KEY_PREFIX + tourId) === 'true';
  }

  markTourSeen(tourId: string): void {
    localStorage.setItem(TOUR_KEY_PREFIX + tourId, 'true');
  }

  resetTour(tourId: string): void {
    localStorage.removeItem(TOUR_KEY_PREFIX + tourId);
  }

  resetAllTours(): void {
    Object.keys(localStorage)
      .filter(k => k.startsWith(TOUR_KEY_PREFIX))
      .forEach(k => localStorage.removeItem(k));
  }

  /**
   * One-off contextual call-out, e.g. pointing at a specific element to
   * guide the next click. Unlike startTour(), this has no tourId and is
   * never marked "seen" — it's meant to fire every time it's triggered
   * (such as each time a guide step routes someone through a page), not
   * once-ever like the onboarding tours.
   */
  showHint(steps: DriveStep[]): void {
    if (!steps.length) return;
    if (this.driverInstance) this.driverInstance.destroy();

    this.active$.next(true);
    this.driverInstance = driver({
      showProgress: false,
      animate: true,
      overlayColor: 'rgba(0, 0, 0, 0.7)',
      stagePadding: 8,
      stageRadius: 8,
      popoverClass: 'h2s-tour-popover',
      // Single-step hint — no "Previous" to go back to, and driver.js
      // swaps in doneBtnText (not nextBtnText) for the last/only step, so
      // both need to say the same thing for a consistent single button.
      showButtons: ['next', 'close'],
      nextBtnText: 'Got it',
      doneBtnText: 'Got it',
      steps,
      onDestroyStarted: () => {
        this.driverInstance?.destroy();
        this.driverInstance = null;
        this.active$.next(false);
      },
    });

    this.driverInstance.drive();
  }

  private launchTour(tourId: string, steps: DriveStep[]): void {
    // Destroy any previous instance
    if (this.driverInstance) {
      this.driverInstance.destroy();
    }

    this.driverInstance = driver({
      showProgress: true,
      animate: true,
      overlayColor: 'rgba(0, 0, 0, 0.7)',
      stagePadding: 8,
      stageRadius: 8,
      popoverClass: 'h2s-tour-popover',
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Done',
      steps,
      onDestroyStarted: () => {
        this.markTourSeen(tourId);
        this.driverInstance?.destroy();
        this.driverInstance = null;
      },
    });

    this.driverInstance.drive();
  }
}
